import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { CheckCircle, Bell, Trophy, AlertCircle, Loader2, Mail, MessageCircle, Shuffle, Globe2 } from 'lucide-react';

interface NotificationPreferencesData {
  // Email preferences
  pick_confirmations: boolean;
  pick_reminders: boolean;
  gameweek_results: boolean;
  knockout_notifications: boolean;
  competition_updates: boolean;
  organization_updates: boolean;
  prediction_confirmations: boolean;
  predictor_results: boolean;
  winner_announcement: boolean;
  auto_pick: boolean;
  wc_submission_reminders: boolean;
  // WhatsApp preferences
  pick_confirmations_whatsapp: boolean;
  pick_reminders_whatsapp: boolean;
  gameweek_results_whatsapp: boolean;
  knockout_notifications_whatsapp: boolean;
  competition_updates_whatsapp: boolean;
  prediction_confirmations_whatsapp: boolean;
  predictor_results_whatsapp: boolean;
  winner_announcement_whatsapp: boolean;
  auto_pick_whatsapp: boolean;
  wc_submission_reminders_whatsapp: boolean;
}

const defaultPreferences: NotificationPreferencesData = {
  pick_confirmations: true,
  pick_reminders: true,
  gameweek_results: true,
  knockout_notifications: true,
  competition_updates: true,
  organization_updates: true,
  prediction_confirmations: true,
  predictor_results: true,
  winner_announcement: true,
  auto_pick: true,
  wc_submission_reminders: true,
  pick_confirmations_whatsapp: false,
  pick_reminders_whatsapp: true,
  gameweek_results_whatsapp: false,
  knockout_notifications_whatsapp: true,
  competition_updates_whatsapp: false,
  prediction_confirmations_whatsapp: false,
  predictor_results_whatsapp: false,
  winner_announcement_whatsapp: true,
  auto_pick_whatsapp: true,
  wc_submission_reminders_whatsapp: true,
};

interface NotificationPreferencesSectionProps {
  hasVerifiedPhone?: boolean;
}

export const NotificationPreferencesSection: React.FC<NotificationPreferencesSectionProps> = ({
  hasVerifiedPhone = false
}) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferencesData>(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_email_preferences')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          pick_confirmations: data.pick_confirmations ?? true,
          pick_reminders: data.pick_reminders ?? true,
          gameweek_results: data.gameweek_results ?? true,
          knockout_notifications: data.knockout_notifications ?? true,
          competition_updates: data.competition_updates ?? true,
          organization_updates: data.organization_updates ?? true,
          prediction_confirmations: data.prediction_confirmations ?? true,
          predictor_results: data.predictor_results ?? true,
          winner_announcement: data.winner_announcement ?? true,
          auto_pick: data.auto_pick ?? true,
          pick_confirmations_whatsapp: data.pick_confirmations_whatsapp ?? false,
          pick_reminders_whatsapp: data.pick_reminders_whatsapp ?? true,
          gameweek_results_whatsapp: data.gameweek_results_whatsapp ?? false,
          knockout_notifications_whatsapp: data.knockout_notifications_whatsapp ?? true,
          competition_updates_whatsapp: data.competition_updates_whatsapp ?? false,
          prediction_confirmations_whatsapp: data.prediction_confirmations_whatsapp ?? false,
          predictor_results_whatsapp: data.predictor_results_whatsapp ?? false,
          winner_announcement_whatsapp: data.winner_announcement_whatsapp ?? true,
          auto_pick_whatsapp: data.auto_pick_whatsapp ?? true,
          wc_submission_reminders: data.wc_submission_reminders ?? true,
          wc_submission_reminders_whatsapp: data.wc_submission_reminders_whatsapp ?? true,
        });
      }
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      setError('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  // Mandatory email notifications that cannot be disabled
  const mandatoryEmailKeys: (keyof NotificationPreferencesData)[] = [
    'knockout_notifications',
    'winner_announcement',
  ];

  const handleToggle = async (key: keyof NotificationPreferencesData) => {
    if (!user) return;
    
    // Prevent toggling mandatory email notifications
    if (mandatoryEmailKeys.includes(key)) {
      return;
    }
    
    // Prevent toggling WhatsApp if phone not verified
    if (key.endsWith('_whatsapp') && !hasVerifiedPhone) {
      return;
    }
    
    // Optimistic update
    const newValue = !preferences[key];
    setPreferences(prev => ({ ...prev, [key]: newValue }));
    
    try {
      setSaving(true);
      setError(null);

      const updateData = {
        ...preferences,
        [key]: newValue,
        updated_at: new Date().toISOString(),
      };

      // Check if record exists first
      const { data: existing } = await supabase
        .from('user_email_preferences')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      let saveError;
      if (existing) {
        // Update existing record
        const result = await supabase
          .from('user_email_preferences')
          .update(updateData)
          .eq('user_id', user.id);
        saveError = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('user_email_preferences')
          .insert({ user_id: user.id, ...updateData });
        saveError = result.error;
      }

      if (saveError) throw saveError;
    } catch (err) {
      console.error('Error saving preference:', err);
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !newValue }));
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-theme-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-destructive text-sm">{error}</p>
        <button
          onClick={fetchPreferences}
          className="mt-2 text-primary text-sm font-medium hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // LMS notification types
  const lmsNotificationTypes = [
    {
      emailKey: 'pick_confirmations',
      whatsappKey: 'pick_confirmations_whatsapp',
      icon: <CheckCircle className="h-4 w-4" />,
      title: 'Pick Confirmations',
      description: 'When you submit a pick',
    },
    {
      emailKey: 'pick_reminders',
      whatsappKey: 'pick_reminders_whatsapp',
      icon: <Bell className="h-4 w-4" />,
      title: 'Pick Reminders',
      description: 'Before deadline expires',
    },
    {
      emailKey: 'auto_pick',
      whatsappKey: 'auto_pick_whatsapp',
      icon: <Shuffle className="h-4 w-4" />,
      title: 'Auto-Pick Alerts',
      description: 'When system picks for you',
    },
    {
      emailKey: 'knockout_notifications',
      whatsappKey: 'knockout_notifications_whatsapp',
      icon: <AlertCircle className="h-4 w-4" />,
      title: 'Knockout Alerts',
      description: 'Elimination notifications',
    },
  ];

  // Predictor notification types
  const predictorNotificationTypes = [
    {
      emailKey: 'prediction_confirmations',
      whatsappKey: 'prediction_confirmations_whatsapp',
      icon: <CheckCircle className="h-4 w-4" />,
      title: 'Prediction Confirmations',
      description: 'When you submit predictions',
    },
    {
      emailKey: 'predictor_results',
      whatsappKey: 'predictor_results_whatsapp',
      icon: <Trophy className="h-4 w-4" />,
      title: 'Predictor Results',
      description: 'Weekly points summary',
    },
  ];

  // World Cup notification types
  const worldCupNotificationTypes = [
    {
      emailKey: 'wc_submission_reminders',
      whatsappKey: 'wc_submission_reminders_whatsapp',
      icon: <Globe2 className="h-4 w-4" />,
      title: 'Submission Reminders',
      description: 'Email at 14d/7d/48h; WhatsApp added at 24h & 2h',
    },
  ];

  // Shared notification types
  const sharedNotificationTypes: Array<{
    emailKey: keyof NotificationPreferencesData;
    whatsappKey?: keyof NotificationPreferencesData;
    icon: React.ReactNode;
    title: string;
    description: string;
  }> = [
    {
      emailKey: 'gameweek_results',
      whatsappKey: 'gameweek_results_whatsapp',
      icon: <Trophy className="h-4 w-4" />,
      title: 'Gameweek Results',
      description: 'When results are finalized',
    },
    {
      emailKey: 'competition_updates',
      whatsappKey: 'competition_updates_whatsapp',
      icon: <Mail className="h-4 w-4" />,
      title: 'Competition News',
      description: 'General announcements',
    },
    {
      emailKey: 'organization_updates',
      icon: <Mail className="h-4 w-4" />,
      title: 'Organisation News',
      description: 'Announcements from your organisation',
    },
    {
      emailKey: 'winner_announcement',
      whatsappKey: 'winner_announcement_whatsapp',
      icon: <Trophy className="h-4 w-4" />,
      title: 'Winner Announcements',
      description: 'Competition winner alerts',
    },
  ];

  const renderNotificationRow = (notification: {
    emailKey: string;
    whatsappKey?: string;
    icon: React.ReactNode;
    title: string;
    description: string;
  }) => {
    const emailKey = notification.emailKey as keyof NotificationPreferencesData;
    const isEmailMandatory = mandatoryEmailKeys.includes(emailKey);
    const hasWhatsapp = Boolean(notification.whatsappKey);
    
    return (
      <NotificationRow
        key={notification.emailKey}
        icon={notification.icon}
        title={notification.title}
        description={notification.description}
        emailEnabled={preferences[emailKey] ?? false}
        whatsappEnabled={
          hasWhatsapp
            ? (preferences[notification.whatsappKey as keyof NotificationPreferencesData] ?? false)
            : false
        }
        onToggleEmail={() => handleToggle(emailKey)}
        onToggleWhatsapp={
          hasWhatsapp
            ? () => handleToggle(notification.whatsappKey as keyof NotificationPreferencesData)
            : () => undefined
        }
        disabled={saving}
        emailDisabled={isEmailMandatory}
        emailMandatory={isEmailMandatory}
        whatsappDisabled={!hasWhatsapp || !hasVerifiedPhone}
      />
    );
  };

  return (
    <div className="space-y-1">
      {/* Header row with channel labels */}
      <div className="flex items-center justify-end pb-2 mb-2 border-b border-muted gap-3">
        <div className="w-16 flex justify-center">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            <span>Email</span>
          </div>
        </div>
        <div className="w-16 flex justify-center">
          <div className={`flex items-center gap-1 text-xs ${hasVerifiedPhone ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>
            <MessageCircle className="h-3.5 w-3.5" />
            <span>WhatsApp</span>
          </div>
        </div>
      </div>

      {!hasVerifiedPhone && (
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-2.5 mb-3">
          <p className="text-xs text-warning">
            Verify your phone number above to enable WhatsApp notifications.
          </p>
        </div>
      )}

      {/* LMS Section */}
      <div className="pt-1">
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Last Man Standing</h5>
        {lmsNotificationTypes.map(renderNotificationRow)}
      </div>

      {/* Predictor Section */}
      <div className="pt-3 border-t border-muted">
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-2">Predictor</h5>
        {predictorNotificationTypes.map(renderNotificationRow)}
      </div>

      {/* World Cup Section */}
      <div className="pt-3 border-t border-muted">
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-2">World Cup</h5>
        {worldCupNotificationTypes.map(renderNotificationRow)}
      </div>


      {/* Shared Section */}
      <div className="pt-3 border-t border-muted">
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-2">General</h5>
        {sharedNotificationTypes.map(renderNotificationRow)}
      </div>

      {saving && (
        <div className="flex items-center justify-center pt-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">Saving...</span>
        </div>
      )}
    </div>
  );
};

interface NotificationRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  emailEnabled: boolean;
  whatsappEnabled: boolean;
  onToggleEmail: () => void;
  onToggleWhatsapp: () => void;
  disabled?: boolean;
  emailDisabled?: boolean;
  emailMandatory?: boolean;
  whatsappDisabled?: boolean;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  icon,
  title,
  description,
  emailEnabled,
  whatsappEnabled,
  onToggleEmail,
  onToggleWhatsapp,
  disabled = false,
  emailDisabled = false,
  emailMandatory = false,
  whatsappDisabled = false,
}) => {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <div className="text-muted-foreground flex-shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground truncate">
            {title}
            {emailMandatory && (
              <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">(required)</span>
            )}
          </h4>
          <p className="text-xs text-muted-foreground truncate">
            {description}
          </p>
        </div>
      </div>
      <div className="flex items-center flex-shrink-0 ml-3 gap-3">
        {/* Email toggle */}
        <div className="w-16 flex justify-center">
          <ToggleSwitch
            enabled={emailEnabled}
            onChange={onToggleEmail}
            disabled={disabled || emailDisabled}
            locked={emailMandatory}
          />
        </div>
        {/* WhatsApp toggle */}
        <div className="w-16 flex justify-center">
          <ToggleSwitch
            enabled={whatsappEnabled}
            onChange={onToggleWhatsapp}
            disabled={disabled || whatsappDisabled}
          />
        </div>
      </div>
    </div>
  );
};

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
  locked?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  enabled,
  onChange,
  disabled = false,
  locked = false,
}) => {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled || locked}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
        enabled ? 'bg-theme-primary' : 'bg-gray-200 dark:bg-gray-700'
      } ${locked ? 'opacity-50 cursor-not-allowed' : disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
      title={locked ? 'This notification is required' : undefined}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
};
